import { LitElement, TemplateResult, html } from 'lit';
import { customElement, queryAssignedElements, property } from 'lit/decorators.js';
import { ResizeController } from '../../controllers/resize-controller';
import globalStyles from './styles/outline-pagination.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

/**
 * The OutlinePagination component
 * @element outline-pagination
 *
 * @extends LitElement
 *
 * @slot pagination
 *
 * @property type - Determines whether it's a full or minimal pagination component.
 * @property size - Determines the font size and spacing.
 */
@customElement('outline-pagination')
export class OutlinePagination extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
  });

  resizeController: ResizeController = new ResizeController(this, {
    breakpoints: [768],
  });

  @property({ type: Boolean, reflect: true })
  isMobile: boolean;

  @queryAssignedElements({flatten: true})
  navRef: Array<HTMLLIElement>

  _sanitized: Array<HTMLLIElement>;

  _index: number;

  _previous: HTMLLIElement | false;

  _current: HTMLLIElement;

  _next: Array<HTMLLIElement> | false;

  updated() {
    this.handleUpdateVisible();
    this.isMobile = this.resizeController.currentBreakpointRange === 0;
  }

  render(): TemplateResult {
    return html`
      <div class="outline-pagination">
        <slot></slot>
      </div>
    `;
  }

  setCurrent() {
    this._current = this._sanitized.filter((el)=> el.classList.contains('is-active'))[0] as HTMLLIElement;
    this._index = this._sanitized.indexOf(this._current);
  }

  setPrevious() {
    if (this._index > 0) {
      this._previous = this._sanitized[this._index - 1];
    } else {
      this._previous = false;
    }
  }

  setNext() {
    const position = this._sanitized.length - this._index;

    if (position >= 2) {
      return this._next = this._sanitized.slice(this._index + 1, this._index + 3);
    } else if (position >= 1) {
      return this._next = this._sanitized.slice(this._index + 1, this._index + 2);
    } else {
      return this._next = false;
    }
  }

  handleUpdateVisible() {
    const lis = this.navRef[0]?.querySelectorAll('li');
    this._sanitized = [...lis].filter((el)=> (
      !el.classList.contains('pager__item--previous') &&
      !el.classList.contains('pager__item--first') &&
      !el.classList.contains('pager__item--ellipsis') &&
      !el.classList.contains('pager__item--next') &&
      !el.classList.contains('pager__item--last'))
    );
    this.handleClassCleanup();
    this.setCurrent();
    this.setPrevious();
    this.setNext();
    this.handleVisibleClass();
  }

  handleVisibleClass() {
    [this._current, this._previous].forEach(el => {
      if (el) {
        el.classList.add('visible')
      }
      if (this._next !== false) {
        this._next.forEach(el => el.classList.add('visible'))
      }
    })
  }

  handleClassCleanup() {
    this._sanitized.forEach(el => el.classList.remove('visible'));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-pagination': OutlinePagination;
  }
}
